import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

function ActiveSessions({ user }) {
  const [sessions, setSessions] = useState([]);
  const [currentTokenId, setCurrentTokenId] = useState("");
  const [loading, setLoading] = useState(false);
  const [openUsers, setOpenUsers] = useState({});

  const role = String(user?.role || "")
    .toLowerCase()
    .trim();
  const isITManager = role === "it manager";

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
  });

  const fetchSessions = async () => {
    try {
      setLoading(true);

      const url = isITManager
        ? "/sessions/all-history"
        : "/sessions/my-history";

      const res = await api.get(url, getAuthHeader());

      setSessions(res.data?.data || []);
      setCurrentTokenId(res.data?.currentTokenId || "");
    } catch (error) {
      alert(error.response?.data?.message || "Failed to fetch sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const groupedSessions = useMemo(() => {
    const map = {};

    sessions.forEach((s) => {
      const userId =
        typeof s.userId === "object" ? s.userId?._id : s.userId || "unknown";

      const name = s.userId?.name || s.userId?.email || "Unknown";
      const email = s.userId?.email || "-";
      const role = s.userId?.role || "-";

      if (!map[userId]) {
        map[userId] = {
          userId,
          name,
          email,
          role,
          sessions: [],
        };
      }

      map[userId].sessions.push(s);
    });

    return Object.values(map);
  }, [sessions]);

  const toggleUser = (userId) => {
    setOpenUsers((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const logoutDevice = async (sessionId) => {
    if (!window.confirm("Logout this device?")) return;

    await api.patch(`/sessions/${sessionId}/logout`, {}, getAuthHeader());
    await fetchSessions();
  };

  const logoutAll = async (userId) => {
    if (!window.confirm("Logout all devices for this employee?")) return;

    await api.patch(`/sessions/logout-all/${userId}`, {}, getAuthHeader());
    await fetchSessions();
  };

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  const getLocation = (s) => {
    const location = [s.city, s.region, s.country].filter(Boolean).join(", ");
    return location || "-";
  };

  const isSessionActive = (s) =>
    !s.logoutAt && !s.loggedOutAt && s.isActive !== false;

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ color: "#343A76" }}>Login Sessions History</h2>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Employee</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Total Sessions</th>
                <th style={th}>Active</th>
                <th style={th}>Last Active</th>
                <th style={th}>Action</th>
              </tr>
            </thead>

            <tbody>
              {groupedSessions.length === 0 ? (
                <tr>
                  <td colSpan={7} style={td}>
                    No sessions found
                  </td>
                </tr>
              ) : (
                groupedSessions.map((emp) => {
                  const isOpen = !!openUsers[emp.userId];

                  const activeCount =
                    emp.sessions.filter(isSessionActive).length;

                  const lastActive = emp.sessions
                    .map((s) => s.lastActiveAt || s.loginAt)
                    .filter(Boolean)
                    .sort((a, b) => new Date(b) - new Date(a))[0];

                  return (
                    <React.Fragment key={emp.userId}>
                      <tr style={{ background: "#f8fafc" }}>
                        <td style={td}>
                          <button
                            onClick={() => toggleUser(emp.userId)}
                            style={expandBtn}
                          >
                            {isOpen ? "▼" : "▶"}
                          </button>
                          <b>{emp.name}</b>
                        </td>

                        <td style={td}>{emp.email}</td>
                        <td style={td}>{emp.role}</td>
                        <td style={td}>{emp.sessions.length}</td>
                        <td style={td}>{activeCount}</td>
                        <td style={td}>{formatDate(lastActive)}</td>

                        <td style={td}>
                          {isITManager && activeCount > 0 ? (
                            <button
                              onClick={() => logoutAll(emp.userId)}
                              style={btnDark}
                            >
                              Logout All
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={innerTd}>
                            <table style={innerTable}>
                              <thead>
                                <tr>
                                  <th style={innerTh}>Browser</th>
                                  <th style={innerTh}>OS</th>
                                  <th style={innerTh}>Device</th>
                                  <th style={innerTh}>IP</th>
                                  <th style={innerTh}>Location</th>
                                  <th style={innerTh}>Login Time</th>
                                  <th style={innerTh}>Last Active</th>
                                  <th style={innerTh}>Logout Time</th>
                                  <th style={innerTh}>Status</th>
                                  <th style={innerTh}>Action</th>
                                </tr>
                              </thead>

                              <tbody>
                                {emp.sessions.map((s) => {
                                  const isCurrent =
                                    s.tokenId === currentTokenId;
                                  const isActive = isSessionActive(s);

                                  return (
                                    <tr key={s._id}>
                                      <td style={innerCell}>
                                        {s.browser || "-"}
                                      </td>
                                      <td style={innerCell}>{s.os || "-"}</td>
                                      <td style={innerCell}>
                                        {s.device || "-"}
                                      </td>
                                      <td style={innerCell}>
                                        {s.ipAddress || "-"}
                                      </td>
                                      <td style={innerCell}>
                                        {getLocation(s)}
                                      </td>
                                      <td style={innerCell}>
                                        {formatDate(s.loginAt)}
                                      </td>
                                      <td style={innerCell}>
                                        {formatDate(s.lastActiveAt)}
                                      </td>
                                      <td style={innerCell}>
                                        {formatDate(
                                          s.logoutAt || s.loggedOutAt,
                                        )}
                                      </td>

                                      <td style={innerCell}>
                                        {isCurrent ? (
                                          <span style={badgeGreen}>
                                            Current
                                          </span>
                                        ) : isActive ? (
                                          <span style={badgeBlue}>Active</span>
                                        ) : (
                                          <span style={badgeGray}>
                                            Logged Out
                                          </span>
                                        )}
                                      </td>

                                      <td style={innerCell}>
                                        {isActive ? (
                                          <button
                                            onClick={() => logoutDevice(s._id)}
                                            style={btnRed}
                                          >
                                            Logout
                                          </button>
                                        ) : (
                                          "-"
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
  minWidth: 1100,
};

const th = {
  background: "#343A76",
  color: "#fff",
  padding: 10,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const td = {
  padding: 10,
  borderBottom: "1px solid #ddd",
  whiteSpace: "nowrap",
};

const innerTd = {
  padding: 12,
  background: "#eef2ff",
  borderBottom: "1px solid #ddd",
};

const innerTable = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
};

const innerTh = {
  background: "#4f56a6",
  color: "#fff",
  padding: 8,
  textAlign: "left",
  whiteSpace: "nowrap",
};

const innerCell = {
  padding: 8,
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const expandBtn = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 16,
  marginRight: 8,
};

const badgeGreen = {
  background: "#dcfce7",
  color: "#166534",
  padding: "5px 10px",
  borderRadius: 20,
  fontWeight: "bold",
};

const badgeBlue = {
  background: "#dbeafe",
  color: "#1d4ed8",
  padding: "5px 10px",
  borderRadius: 20,
  fontWeight: "bold",
};

const badgeGray = {
  background: "#e5e7eb",
  color: "#374151",
  padding: "5px 10px",
  borderRadius: 20,
};

const btnRed = {
  background: "#dc2626",
  color: "#fff",
  border: "none",
  padding: "6px 10px",
  borderRadius: 6,
  cursor: "pointer",
};

const btnDark = {
  background: "#343A76",
  color: "#fff",
  border: "none",
  padding: "6px 10px",
  borderRadius: 6,
  cursor: "pointer",
};

export default ActiveSessions;
